import { useState } from 'react';
import { PhoneCall, Clock } from 'lucide-react';
import { selectPath } from '../../api/forms';
import { AI_CONSENT_TEXT_VERSION, PRIVACY_URL } from '../../lib/constants';

interface Props {
  leadId: string;
  onDispatched: () => void;
}

// NOTE: ENTWURF des KI-Einwilligungstexts (DSGVO + EU AI Act Art. 50 –
// Offenlegung der KI-Interaktion). Vor Go-live anwaltlich bzw. über eRecht24
// freigeben (siehe CLAUDE.md §13).
export default function PathAiCall({ leadId, onDispatched }: Props) {
  const [mode, setMode] = useState<'choose' | 'schedule'>('choose');
  const [aiConsent, setAiConsent] = useState(false);
  const [preferred, setPreferred] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = async (extra: Record<string, unknown>) => {
    if (!aiConsent) return;
    setError(null);
    setBusy(true);
    try {
      await selectPath(leadId, 'ai_call', {
        consent_ai_call: true,
        consent_ai_text_version: AI_CONSENT_TEXT_VERSION,
        ...extra,
      });
      onDispatched();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="rf-card">
      {error && <div className="rf-alert rf-alert--error">{error}</div>}

      {/* KI-Transparenz (EU AI Act): Hinweis + Einwilligung erst hier, weil sich
          die Person aktiv für den KI-Anruf entscheidet. Ohne Häkchen sind beide
          Optionen gesperrt. */}
      <div className="rf-consent" style={{ marginBottom: '1.25rem' }}>
        <label className="rf-check">
          <input
            type="checkbox"
            checked={aiConsent}
            onChange={(e) => setAiConsent(e.target.checked)}
          />
          <span className="rf-check__text">
            Ich möchte telefonisch kontaktiert werden und willige ein, dass dieser
            Anruf durch einen <strong>KI-gestützten Sprachassistenten</strong>{' '}
            erfolgt. Mir ist bewusst, dass ich dabei mit einem automatisierten
            KI-System spreche; alternativ kann ich den Rückruf durch eine:n
            Mitarbeiter:in wählen. Die Einwilligung ist freiwillig und jederzeit
            mit Wirkung für die Zukunft widerrufbar. Mehr dazu in der{' '}
            <a className="rf-link" href={PRIVACY_URL} target="_blank" rel="noreferrer">
              Datenschutzerklärung
            </a>
            <span className="rf-req">*</span>
          </span>
        </label>
      </div>

      {mode === 'choose' && (
        <div className="rf-choices">
          <button
            className="rf-choice"
            disabled={busy || !aiConsent}
            onClick={() => dispatch({ callback_requested: false })}
          >
            <span className="rf-choice__icon"><PhoneCall size={22} /></span>
            <span>
              <span className="rf-choice__title">Jetzt anrufen lassen</span>
              <span className="rf-choice__desc">Unser KI-Telefonassistent ruft dich gleich an.</span>
            </span>
          </button>
          <button
            className="rf-choice"
            disabled={busy || !aiConsent}
            onClick={() => setMode('schedule')}
          >
            <span className="rf-choice__icon"><Clock size={22} /></span>
            <span>
              <span className="rf-choice__title">Rückruf zur Wunschzeit</span>
              <span className="rf-choice__desc">Sag uns, wann es dir am besten passt.</span>
            </span>
          </button>
        </div>
      )}

      {mode === 'schedule' && (
        <div>
          <div className="rf-field">
            <label className="rf-label">Wann sollen wir anrufen?</label>
            <input
              type="datetime-local"
              className="rf-input"
              value={preferred}
              onChange={(e) => setPreferred(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary btn-block"
            disabled={busy || !preferred || !aiConsent}
            onClick={() => dispatch({ callback_requested: true, preferred_time: preferred })}
          >
            {busy ? <span className="rf-spinner" /> : 'Rückruf anfragen'}
          </button>
          <div className="rf-back">
            <button className="btn btn-ghost" disabled={busy} onClick={() => setMode('choose')}>
              Zurück
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
